import React, { useState, useEffect, useCallback } from 'react';
import configSistemaService from '../services/configSistema.service';
import feriadoService        from '../services/feriado.service';

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  borda:   '#e8eaed', cinzaBg: '#fafafa',
  texto:   '#1a1a2e', muted:   '#888',
  branco:  '#fff',
};

const DIAS = [
  { v: 1, n: 'Segunda' }, { v: 2, n: 'Terça' }, { v: 3, n: 'Quarta' },
  { v: 4, n: 'Quinta' },  { v: 5, n: 'Sexta' }, { v: 6, n: 'Sábado' },
  { v: 7, n: 'Domingo' },
];

const inputStyle = {
  width: '100%', height: '34px', padding: '0 10px',
  fontSize: '13px', fontFamily: 'inherit',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  color: COR.texto, boxSizing: 'border-box',
};

// ════════════════════════════════════════════════════════════════════════════
// ConfiguracoesSistemaModal — modal grande com tabs (Horário Global / Feriados)
// Props:
//   aberto, usuario, onFechar
// ════════════════════════════════════════════════════════════════════════════
function ConfiguracoesSistemaModal({ aberto, usuario, onFechar }) {
  const [tabAtiva, setTabAtiva] = useState('horario');

  if (!aberto) return null;

  return (
    <>
      <div onClick={onFechar} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '900px', maxHeight: '88vh',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
        zIndex: 10000, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '12px',
          backgroundColor: COR.azul, color: COR.branco,
        }}>
          <span style={{ fontSize: '18px' }}>⚙️</span>
          <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
            Configurações do Sistema
          </span>
          <button onClick={onFechar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', borderBottom: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
        }}>
          {[
            { id: 'horario',  label: '🕐 Horário Comercial' },
            { id: 'feriados', label: '📅 Feriados' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTabAtiva(t.id)}
              style={{
                padding: '10px 20px', fontSize: '13px', fontWeight: '600',
                border: 'none', cursor: 'pointer',
                backgroundColor: tabAtiva === t.id ? COR.branco : 'transparent',
                color:           tabAtiva === t.id ? COR.azul   : COR.muted,
                borderBottom: tabAtiva === t.id ? `3px solid ${COR.azul}` : '3px solid transparent',
              }}
            >{t.label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {tabAtiva === 'horario'  && <TabHorarioGlobal usuario={usuario} />}
          {tabAtiva === 'feriados' && <TabFeriados      usuario={usuario} />}
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 1 — Horário comercial GLOBAL (default do sistema)
// ════════════════════════════════════════════════════════════════════════════
function TabHorarioGlobal({ usuario }) {
  const [configs, setConfigs] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [msg, setMsg] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const lista = await configSistemaService.listar('horario.');
      const map = {};
      lista.forEach(c => { map[c.chave] = c.valor; });
      setConfigs(map);
    } catch (err) {
      console.error(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const setChave = (chave, valor) => {
    setConfigs(c => ({ ...c, [chave]: valor }));
  };

  const salvar = async () => {
    setSalvando(true); setMsg('');
    try {
      await configSistemaService.atualizarLote(configs, usuario?.id);
      setMsg('✓ Configurações salvas com sucesso.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Erro ao salvar: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return <div style={{ padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px' }}>
      Carregando...
    </div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '16px', fontSize: '12px', color: COR.muted }}>
        Configura o horário comercial padrão do sistema. Cada colaborador pode
        ter um horário próprio em <b>Horários por Colaborador</b>.
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px',
        backgroundColor: COR.cinzaBg, padding: '16px', borderRadius: '6px',
        border: `1px solid ${COR.borda}`,
      }}>
        <Campo label="Dia início da semana">
          <select
            value={configs['horario.dia_semana_inicio'] || '1'}
            onChange={e => setChave('horario.dia_semana_inicio', e.target.value)}
            style={inputStyle}
          >
            {DIAS.map(d => <option key={d.v} value={d.v}>{d.n}</option>)}
          </select>
        </Campo>
        <Campo label="Dia fim da semana">
          <select
            value={configs['horario.dia_semana_fim'] || '5'}
            onChange={e => setChave('horario.dia_semana_fim', e.target.value)}
            style={inputStyle}
          >
            {DIAS.map(d => <option key={d.v} value={d.v}>{d.n}</option>)}
          </select>
        </Campo>

        <Campo label="Hora início">
          <input type="time"
            value={configs['horario.hora_inicio'] || '09:00'}
            onChange={e => setChave('horario.hora_inicio', e.target.value)}
            style={inputStyle} />
        </Campo>
        <Campo label="Hora fim">
          <input type="time"
            value={configs['horario.hora_fim'] || '18:00'}
            onChange={e => setChave('horario.hora_fim', e.target.value)}
            style={inputStyle} />
        </Campo>

        <Campo label="Início do almoço">
          <input type="time"
            value={configs['horario.hora_almoco_ini'] || '12:00'}
            onChange={e => setChave('horario.hora_almoco_ini', e.target.value)}
            style={inputStyle} />
        </Campo>
        <Campo label="Fim do almoço">
          <input type="time"
            value={configs['horario.hora_almoco_fim'] || '13:00'}
            onChange={e => setChave('horario.hora_almoco_fim', e.target.value)}
            style={inputStyle} />
        </Campo>
      </div>

      {msg && (
        <div style={{
          marginTop: '14px', padding: '8px 12px',
          backgroundColor: msg.startsWith('✓') ? COR.verdeBg : COR.vermBg,
          color: msg.startsWith('✓') ? COR.verde : COR.vermelho,
          borderRadius: '5px', fontSize: '12px',
          border: `1px solid ${msg.startsWith('✓') ? COR.verde : COR.vermelho}33`,
        }}>{msg}</div>
      )}

      <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={salvar} disabled={salvando} style={{
          padding: '8px 22px', fontSize: '13px', fontWeight: '600',
          border: 'none', borderRadius: '5px',
          backgroundColor: salvando ? '#cccccc' : COR.azul,
          color: COR.branco, cursor: salvando ? 'not-allowed' : 'pointer',
        }}>{salvando ? 'Salvando...' : '💾 Salvar'}</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 2 — Feriados (CRUD + importar ano)
// ════════════════════════════════════════════════════════════════════════════
function TabFeriados({ usuario }) {
  const [feriados, setFeriados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [importando, setImportando] = useState(false);

  // Form de criar/editar
  const [editando, setEditando] = useState(null); // null = criar
  const [form, setForm] = useState({ data: '', descricao: '', uf: '', tipo: 'F', observacao: '' });
  const [erroForm, setErroForm] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const lista = await feriadoService.listar(ano);
      setFeriados(lista);
    } catch (err) {
      console.error(err);
    } finally {
      setCarregando(false);
    }
  }, [ano]);

  useEffect(() => { carregar(); }, [carregar]);

  const formatDataBR = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yy = d.getUTCFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  const dataISO = (iso) => iso ? new Date(iso).toISOString().slice(0, 10) : '';

  const abrirCriar = () => {
    setEditando(null);
    setForm({ data: `${ano}-01-01`, descricao: '', uf: '', tipo: 'F', observacao: '' });
    setErroForm('');
  };

  const abrirEditar = (f) => {
    setEditando(f);
    setForm({
      data: dataISO(f.data),
      descricao: f.descricao,
      uf: f.uf || '',
      tipo: f.tipo || 'F',
      observacao: f.observacao || '',
    });
    setErroForm('');
  };

  const salvarFeriado = async () => {
    setErroForm('');
    if (!form.data) return setErroForm('Data é obrigatória.');
    if (!form.descricao.trim()) return setErroForm('Descrição é obrigatória.');

    try {
      if (editando) {
        await feriadoService.atualizar(editando.id, form);
      } else {
        await feriadoService.criar({ ...form, usuario_id: usuario?.id });
      }
      setEditando(false);
      setForm({ data: '', descricao: '', uf: '', tipo: 'F', observacao: '' });
      await carregar();
    } catch (err) {
      setErroForm(err.response?.data?.mensagem || 'Erro ao salvar.');
    }
  };

  const excluir = async (f) => {
    if (!window.confirm(`Excluir o feriado "${f.descricao}" (${formatDataBR(f.data)})?`)) return;
    try {
      await feriadoService.excluir(f.id);
      await carregar();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao excluir.');
    }
  };

  const importarAno = async () => {
    if (!window.confirm(
      `Importar feriados de ${ano}?\n\nVai calcular automaticamente Carnaval, Sexta Santa, Corpus Christi e adicionar os feriados nacionais + Curitiba. Feriados já existentes serão pulados.`
    )) return;
    setImportando(true);
    try {
      const r = await feriadoService.importarAno(ano, usuario?.id);
      alert(r.mensagem);
      await carregar();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao importar.');
    } finally {
      setImportando(false);
    }
  };

  const labelUf = (uf) => {
    if (!uf)            return { texto: 'Nacional', cor: COR.azul,    bg: COR.azulBg };
    if (uf === 'PR-CT') return { texto: 'Curitiba', cor: COR.verde,   bg: COR.verdeBg };
    return                     { texto: uf,         cor: COR.amarelo, bg: COR.amlBg };
  };

  return (
    <div>
      {/* Toolbar: filtro por ano + botão importar + botão criar */}
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap',
        marginBottom: '14px',
      }}>
        <label style={{ fontSize: '12px', color: COR.muted, fontWeight: '600' }}>Ano:</label>
        <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))}
          style={{ ...inputStyle, width: '90px' }}>
          {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 1 + i).map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        <button onClick={importarAno} disabled={importando} style={{
          padding: '8px 14px', fontSize: '12px', fontWeight: '600',
          border: `1px solid ${COR.verde}`, borderRadius: '5px',
          backgroundColor: COR.verdeBg, color: COR.verde,
          cursor: importando ? 'not-allowed' : 'pointer',
        }}>{importando ? '⏳ Importando...' : `📥 Importar feriados de ${ano}`}</button>

        <div style={{ flex: 1 }} />

        <button onClick={abrirCriar} style={{
          padding: '8px 14px', fontSize: '12px', fontWeight: '600',
          border: 'none', borderRadius: '5px',
          backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
        }}>+ Novo Feriado</button>
      </div>

      {/* Form de criar/editar */}
      {(editando !== false) && (
        <div style={{
          backgroundColor: COR.cinzaBg, padding: '14px', borderRadius: '6px',
          border: `1px solid ${COR.borda}`, marginBottom: '14px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: COR.texto, marginBottom: '10px' }}>
            {editando ? `Editando: ${editando.descricao}` : 'Novo Feriado'}
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: '140px 1fr 130px 100px',
            gap: '10px', marginBottom: '8px',
          }}>
            <Campo label="Data">
              <input type="date" value={form.data}
                onChange={e => setForm(f => ({ ...f, data: e.target.value }))}
                style={inputStyle} />
            </Campo>
            <Campo label="Descrição">
              <input value={form.descricao}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                style={inputStyle} />
            </Campo>
            <Campo label="Abrangência">
              <select value={form.uf}
                onChange={e => setForm(f => ({ ...f, uf: e.target.value }))}
                style={inputStyle}>
                <option value="">Nacional</option>
                <option value="PR">Paraná (PR)</option>
                <option value="PR-CT">Curitiba (PR-CT)</option>
              </select>
            </Campo>
            <Campo label="Tipo">
              <select value={form.tipo}
                onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                style={inputStyle}>
                <option value="F">Fixo</option>
                <option value="V">Variável</option>
              </select>
            </Campo>
          </div>
          <Campo label="Observação">
            <input value={form.observacao}
              onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))}
              style={inputStyle} placeholder="Opcional" />
          </Campo>
          {erroForm && (
            <div style={{
              marginTop: '8px', padding: '6px 10px',
              backgroundColor: COR.vermBg, color: COR.vermelho,
              borderRadius: '4px', fontSize: '11px',
            }}>{erroForm}</div>
          )}
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
            <button onClick={() => setEditando(false)} style={{
              padding: '6px 14px', fontSize: '12px', fontWeight: '600',
              border: `1px solid ${COR.borda}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
            }}>Cancelar</button>
            <button onClick={salvarFeriado} style={{
              padding: '6px 18px', fontSize: '12px', fontWeight: '600',
              border: 'none', borderRadius: '5px',
              backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
            }}>{editando ? 'Atualizar' : 'Criar'}</button>
          </div>
        </div>
      )}

      {/* Lista */}
      {carregando ? (
        <div style={{ padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px' }}>
          Carregando...
        </div>
      ) : feriados.length === 0 ? (
        <div style={{
          padding: '24px', textAlign: 'center', color: COR.muted, fontSize: '12px',
          border: `1px dashed ${COR.borda}`, borderRadius: '6px',
        }}>
          Nenhum feriado cadastrado em {ano}.<br />
          Use o botão <b>📥 Importar feriados de {ano}</b> pra cadastrar automaticamente.
        </div>
      ) : (
        <div style={{
          border: `1px solid ${COR.borda}`, borderRadius: '6px', overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '110px 1fr 110px 60px 110px',
            backgroundColor: COR.cinzaBg, padding: '8px 12px',
            fontSize: '11px', fontWeight: '600', color: COR.muted,
            borderBottom: `1px solid ${COR.borda}`,
          }}>
            <div>DATA</div>
            <div>DESCRIÇÃO</div>
            <div>ABRANGÊNCIA</div>
            <div>TIPO</div>
            <div style={{ textAlign: 'right' }}>AÇÕES</div>
          </div>
          {feriados.map(f => {
            const lab = labelUf(f.uf);
            return (
              <div key={f.id} style={{
                display: 'grid', gridTemplateColumns: '110px 1fr 110px 60px 110px',
                padding: '8px 12px', fontSize: '12px', alignItems: 'center',
                borderBottom: `1px solid ${COR.borda}`,
              }}>
                <div style={{ color: COR.texto, fontWeight: '500' }}>
                  {formatDataBR(f.data)}
                </div>
                <div style={{ color: COR.texto }}>
                  {f.descricao}
                  {f.observacao && (
                    <div style={{ fontSize: '10px', color: COR.muted, marginTop: '2px' }}>
                      {f.observacao}
                    </div>
                  )}
                </div>
                <div>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px',
                    borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                    backgroundColor: lab.bg, color: lab.cor,
                    border: `1px solid ${lab.cor}33`,
                  }}>{lab.texto}</span>
                </div>
                <div style={{ color: COR.muted, fontSize: '11px' }}>
                  {f.tipo === 'V' ? 'Variável' : 'Fixo'}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
                  <button onClick={() => abrirEditar(f)} title="Editar" style={{
                    border: 'none', background: 'transparent',
                    color: COR.azul, cursor: 'pointer', fontSize: '13px', padding: '2px 6px',
                  }}>✏️</button>
                  <button onClick={() => excluir(f)} title="Excluir" style={{
                    border: 'none', background: 'transparent',
                    color: COR.vermelho, cursor: 'pointer', fontSize: '13px', padding: '2px 6px',
                  }}>🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Componente Campo (label + input/conteúdo) ───────────────────────────────
function Campo({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: '11px', fontWeight: '600',
        color: COR.muted, marginBottom: '4px',
      }}>{label}</label>
      {children}
    </div>
  );
}

export default ConfiguracoesSistemaModal;
